
<script setup>
import { computed, ref } from "vue";
import { useRouter } from "vue-router";
import MainLayout from "@/components/layout/MainLayout.vue";
import PageHeader from "@/components/common/PageHeader.vue";
import InvoiceStatCard from "@/components/invoices/InvoiceStatCard.vue";
import InvoiceFilterBar from "@/components/invoices/InvoiceFilterBar.vue";
import InvoiceTable from "@/components/invoices/InvoiceTable.vue";
import InvoiceFormModal from "@/components/invoices/InvoiceFormModal.vue";
import { useInvoiceStore } from "@/stores/invoice";

const router = useRouter();
const invoiceStore = useInvoiceStore();

const invoiceFormModal = ref(null);

const search = computed({
  get: () => invoiceStore.filters.search,
  set: (value) => invoiceStore.setSearch(value),
});

const billingMonth = computed({
  get: () => invoiceStore.filters.billingMonth,
  set: (value) => invoiceStore.setBillingMonth(value),
});

const status = computed({
  get: () => invoiceStore.filters.status,
  set: (value) => invoiceStore.setStatus(value),
});

const formatCurrency = (value) => {
  return new Intl.NumberFormat("th-TH", {
    style: "currency",
    currency: "THB",
    minimumFractionDigits: 0,
  }).format(value || 0);
};

const handleViewInvoice = (invoice) => {
  if (!invoice?.id) return;
  router.push(`/invoices/${invoice.id}`);
};

const handleEditInvoice = (invoice) => invoiceFormModal.value?.open(invoiceStore.getInvoiceById(invoice.id));
</script>

<template>
  <MainLayout>
    <div class="container-fluid px-0">
      <PageHeader
        title="รายการใบแจ้งหนี้"
        subtitle="จัดการการออกบิลค่าส่วนกลาง ติดตามสถานะการชำระ และตรวจสอบยอดค้างชำระ"
        button-text="ออกใบแจ้งหนี้"
        button-icon="bi-receipt-cutoff"
        @action="invoiceFormModal.open()"
      />

      <div class="row g-4 mb-4">
        <div class="col-md-6 col-xl-3">
          <InvoiceStatCard
            title="ใบแจ้งหนี้ทั้งหมด"
            :value="invoiceStore.totalInvoices.toLocaleString('th-TH')"
            subtitle="จำนวนบิลทั้งหมดในระบบ"
            icon="bi-receipt"
            color="primary"
          />
        </div>

        <div class="col-md-6 col-xl-3">
          <InvoiceStatCard
            title="ชำระแล้ว"
            :value="invoiceStore.paidInvoices.toLocaleString('th-TH')"
            subtitle="บิลที่ชำระครบแล้ว"
            icon="bi-check-circle"
            color="success"
          />
        </div>

        <div class="col-md-6 col-xl-3">
          <InvoiceStatCard
            title="ค้างชำระ"
            :value="invoiceStore.overdueInvoices.toLocaleString('th-TH')"
            subtitle="บิลที่เกินกำหนดชำระ"
            icon="bi-exclamation-circle"
            color="danger"
          />
        </div>

        <div class="col-md-6 col-xl-3">
          <InvoiceStatCard
            title="ยอดค้างรวม"
            :value="formatCurrency(invoiceStore.totalOutstanding)"
            subtitle="ยอดที่ยังไม่ได้รับชำระ"
            icon="bi-cash-stack"
            color="warning"
          />
        </div>
      </div>

      <InvoiceFilterBar
        :search="search"
        :billing-month="billingMonth"
        :status="status"
        :billing-month-options="invoiceStore.billingMonthOptions"
        :status-options="invoiceStore.statusOptions"
        @update:search="search = $event"
        @update:billingMonth="billingMonth = $event"
        @update:status="status = $event"
        @reset="invoiceStore.resetFilters()"
      />

      <div class="row g-4 mb-4">
        <div class="col-12">
          <div
            class="card border-0 shadow-sm rounded-4 bg-primary bg-opacity-10"
          >
            <div class="card-body p-4">
              <div
                class="d-flex flex-column flex-lg-row justify-content-between align-items-lg-center gap-3"
              >
                <div>
                  <div class="fw-bold fs-5 mb-1">
                    ยอดรับชำระรวมจากใบแจ้งหนี้
                  </div>
                  <div class="text-muted">
                    สรุปรายรับที่เก็บได้จากทุกใบแจ้งหนี้ในระบบ
                  </div>
                </div>

                <div class="text-lg-end">
                  <div class="display-6 fw-bold text-primary mb-0">
                    {{ formatCurrency(invoiceStore.totalRevenue) }}
                  </div>
                  <div class="text-muted small">ยอดรับชำระสะสม</div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>

      <InvoiceTable
        :items="invoiceStore.filteredInvoices"
        @view="handleViewInvoice"
        @edit="handleEditInvoice"
      />
      <!-- ✅ Mount modal ไว้ที่นี่ -->
      <InvoiceFormModal ref="invoiceFormModal" />
    </div>
  </MainLayout>
</template>
